import React, { useCallback, useEffect, useState } from "react";
import { Modal, Drawer, Button, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import HtmlReportWidgets from "../../views/ReportHtmlView/HtmlReportWidgets";
import LoadingAnimation from "../common/LoadingAnimation";
import ReportIcon from "../../assets/icons/report-dash-icon.svg";
import DownloadIcon from "../../assets/icons/downlode.svg";
import "./DrillDownTarget.css";

const { REACT_APP_CONNECTION_INFO_BASE_URL } = process.env;

/**
 * Renders a drill-down target report inside a Modal or a Side Panel.
 *
 * The target board is fetched locally on purpose - getBoardByIdAsync writes to
 * boards.selectedBoard, which belongs to the board underneath, and reusing it
 * would wipe out the view the user drilled down from.
 */
export default function DrillDownTarget({ open, mode, boardId, params, onClose }) {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportReady, setReportReady] = useState(false);
  const [exporting, setExporting] = useState(false);
  /* The report's own export routine, handed up by the widget inside it - see
     onRegisterExport in HtmlReportWidgets. Held in state rather than a ref so the footer
     re-renders when it arrives and the button can enable itself. */
  const [exportReport, setExportReport] = useState(null);
  const registerExport = useCallback((fn) => setExportReport(() => fn), []);

  useEffect(() => {
    if (!open || !boardId) return undefined;

    let cancelled = false;
    setLoading(true);
    setBoard(null);
    setReportReady(false);
    // destroyOnClose unmounts the report, so last open's export routine must not linger
    setExportReport(null);

    fetch(`${REACT_APP_CONNECTION_INFO_BASE_URL}/dashboard-details/${boardId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
    })
      .then((response) => response.json())
      .then((data) => {
        if (cancelled) return;
        if (data?.data) {
          setBoard(data.data);
          // a board with no widgets never reports itself ready
          if (!data.data.resources?.length) setReportReady(true);
        } else {
          message.error("Drill-down report could not be loaded");
        }
      })
      .catch(() => {
        if (!cancelled) message.error("Drill-down report could not be loaded");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, boardId]);

  // if a resource request fails the report never reports itself ready, so give up waiting
  useEffect(() => {
    if (!board || reportReady) return undefined;
    const timer = setTimeout(() => setReportReady(true), 20000);
    return () => clearTimeout(timer);
  }, [board, reportReady]);

  /* ── EXPORT ──
     Built in the browser from the rows the panel is already showing, by the same routine
     behind every other download in the app - see utils/widgetExport.

     It used to POST to /generate-pdf-report, which was wrong twice over. That endpoint
     re-fetches the report's source data, so it never saw the drill-down filter and
     returned the whole report rather than the slice the reader had clicked through to -
     the wrong data, reported as a success. It also read req.body.bodyParams, which this
     caller never sent, so it threw before answering at all.

     Exporting here fixes both: the rows are the filtered ones on screen, and the file
     matches what a reader gets from the download menu anywhere else.

     The server endpoint stays for the scheduler, which has no browser to run this in. */
  const handleExportPdf = useCallback(async () => {
    if (typeof exportReport !== "function") return;

    setExporting(true);
    try {
      const written = await exportReport("pdf");
      if (!written) message.warning("There is no data to export in this report");
    } catch (error) {
      console.error("Drill-down export failed:", error);
      message.error("Could not export the PDF");
    } finally {
      setExporting(false);
    }
  }, [exportReport]);

  const title = (
    <div className="drilldown-target-title">
      <span className="drilldown-target-icon">
        <img src={ReportIcon} alt="report" />
      </span>
      <span className="drilldown-target-name">{board?.name || "Drill Down"}</span>
    </div>
  );

  // the report is mounted as soon as the board arrives so it can start fetching its
  // own data, but stays hidden behind the spinner until it has something to draw -
  // otherwise there is a blank gap between "loaded the board" and "rendered the report"
  const isBusy = loading || (board && !reportReady);

  const body = (
    <>
      {/* the values carried over from the clicked row */}
      {params?.length && !isBusy ? (
        <div className="drilldown-target-context">
          {params.map((p) => (
            <span className="drilldown-target-chip" key={`ddp_${p.key}`}>
              <b>{p.key}</b>
              {`: ${p.value ?? ""}`}
            </span>
          ))}
        </div>
      ) : null}

      {/* The report must stay VISIBLE while it loads: react-grid-layout measures its
          container to lay the widgets out, and a hidden container measures 0 - which
          left every widget mis-positioned. So the spinner covers it instead. */}
      <div className="drilldown-target-report">
        {isBusy ? (
          <div className="drilldown-target-overlay">
            <LoadingAnimation text="Loading report…" />
          </div>
        ) : null}

        {board ? (
          <HtmlReportWidgets
            board={board}
            selectedResource={null}
            bodyParams={null}
            drillDownParams={params || []}
            onReportReady={() => setReportReady(true)}
            onRegisterExport={registerExport}
          />
        ) : !loading ? (
          <div className="drilldown-target-loading">No data</div>
        ) : null}
      </div>
    </>
  );

  const footer = (
    <div className="drilldown-target-footer">
      {/* Disabled until the report inside has registered its export - which it does once
          it has rows to draw. A chart-only target never registers one: charts export by
          photographing the drawn chart, which this panel has no handle on, so the button
          stays out rather than failing when pressed. */}
      <Button
        className="drilldown-target-export"
        onClick={handleExportPdf}
        loading={exporting}
        disabled={typeof exportReport !== "function"}
      >
        <img src={DownloadIcon} alt="export" /> Export PDF
      </Button>
      <Button type="primary" className="drilldown-target-close" onClick={onClose}>
        Close
      </Button>
    </div>
  );

  if (mode === "side-panel") {
    return (
      <Drawer
        visible={open}
        onClose={onClose}
        placement="right"
        width={620}
        title={title}
        closeIcon={<CloseOutlined />}
        className="drilldown-target drilldown-target--panel"
        footer={footer}
        destroyOnClose
      >
        {body}
      </Drawer>
    );
  }

  return (
    <Modal
      visible={open}
      onCancel={onClose}
      title={title}
      width={880}
      footer={footer}
      className="drilldown-target drilldown-target--modal"
      destroyOnClose
    >
      {body}
    </Modal>
  );
}
